import { Link, useParams } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'

import { ErrorSurface } from '@/components/error-surface'
import { PageHeader } from '@/components/page-header'
import { RequiresWiderScreen } from '@/components/requires-wider-screen'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { RolePermissionsPanel } from '@/features/admin/role-permissions-panel'
import { useAdminPermissions } from '@/features/admin/permissions'
import { useAdminRoles } from '@/features/admin/queries'

/**
 * One role.
 *
 * There is no `GET /access/roles/{roleId}` in the published contract, so this reads the same list
 * `roles-list.tsx` reads and finds its row by id — see the note on `RoleListItem` in
 * `admin-types.ts`. A role deleted or renamed between the list and this screen renders as "not
 * found" rather than a stale card, because the row simply is not in the list any more either.
 */
export function AdminRoleDetailPage() {
  const { roleId } = useParams({ from: '/authenticated/admin/roles/$roleId' })
  const permissions = useAdminPermissions()
  const query = useAdminRoles()

  const role = query.data?.find((candidate) => candidate.roleId === roleId) ?? null

  return (
    <RequiresWiderScreen
      screen="A role"
      reason="A role's permissions are read and edited together here, against the full catalogue — this is a desk screen."
    >
      <Link
        to="/admin/roles"
        className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-text hover:text-brand-text-hover"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        All roles
      </Link>

      {!permissions.canViewRoles ? (
        <p className="text-sm text-muted">This needs admin.role.view.</p>
      ) : query.isPending ? (
        <div className="space-y-4">
          <Skeleton className="h-10 w-80" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : query.isError ? (
        <ErrorSurface error={query.error} onRetry={() => void query.refetch()} title="Roles could not be read" />
      ) : role === null ? (
        <p className="text-sm text-muted">
          No role with this id is in the register. It may have been renamed or removed.
        </p>
      ) : (
        <>
          <PageHeader
            title={role.name}
            description={role.description}
            badges={
              <>
                {role.isSystemRole ? <Badge tone="brand">System role</Badge> : null}
                {role.forbidsFinancialPermissions ? <Badge tone="warning">Non-financial only</Badge> : null}
                <Badge tone="neutral">
                  {role.userCount} member{role.userCount === 1 ? '' : 's'}
                </Badge>
              </>
            }
          />

          <RolePermissionsPanel role={role} />
        </>
      )}
    </RequiresWiderScreen>
  )
}
